/**
 * Heuristic: does this PDF look like carrier shipping labels (4×6 / GoDEX)?
 * Used before prompting Smoke Drive users to auto-attach.
 */

export const SHIPPING_LABEL_TARGET_WIDTH_MM = 100.076;
export const SHIPPING_LABEL_TARGET_HEIGHT_MM = 150.114;
export const SHIPPING_LABEL_SIZE_TOLERANCE_MM = 2.5;

const NAME_HINT_RE =
  /(shipping|ship.?label|carrier|godex|xam|t24|hermes|dpd|evri|parcelforce|royal.?mail.?label|label.?sheet|4\s*[x×]\s*6)/i;

/**
 * @param {{ name?: string, originalFileName?: string, contentType?: string, type?: string }} item
 */
export function isPdfDriveItem(item) {
  if (!item || item.type === 'folder') return false;
  const mime = String(item.contentType || '').toLowerCase();
  if (mime === 'application/pdf' || mime.includes('pdf')) return true;
  const name = String(item.originalFileName || item.name || '').toLowerCase();
  return name.endsWith('.pdf');
}

/**
 * Fast name/mime gate (no PDF parse).
 * @param {{ name?: string, originalFileName?: string, fileName?: string, contentType?: string }} input
 */
export function shippingLabelNameLooksLikely(input) {
  if (!isPdfDriveItem({ ...input, type: 'file' }) && !String(input?.fileName || '').toLowerCase().endsWith('.pdf')) {
    const name = String(input?.originalFileName || input?.name || input?.fileName || '').toLowerCase();
    if (!name.endsWith('.pdf') && !String(input?.contentType || '').toLowerCase().includes('pdf')) {
      return false;
    }
  }
  const name = String(
    input?.originalFileName || input?.name || input?.fileName || '',
  ).trim();
  return NAME_HINT_RE.test(name);
}

/**
 * @param {{ widthMm: number, heightMm: number }} size
 */
export function shippingLabelPageSizeMatchesTarget(size) {
  const width = Number(size?.widthMm);
  const height = Number(size?.heightMm);
  if (!Number.isFinite(width) || !Number.isFinite(height)) return false;
  const tol = SHIPPING_LABEL_SIZE_TOLERANCE_MM;
  const portrait =
    Math.abs(width - SHIPPING_LABEL_TARGET_WIDTH_MM) < tol &&
    Math.abs(height - SHIPPING_LABEL_TARGET_HEIGHT_MM) < tol;
  const landscape =
    Math.abs(width - SHIPPING_LABEL_TARGET_HEIGHT_MM) < tol &&
    Math.abs(height - SHIPPING_LABEL_TARGET_WIDTH_MM) < tol;
  return portrait || landscape;
}

/**
 * Inspect first-page geometry (pdf-lib). Soft failure → not a label.
 * @param {Blob} blob
 */
export async function inspectShippingLabelPdfGeometry(blob) {
  if (!(blob instanceof Blob) || !blob.size) {
    return { ok: false, reason: 'empty' };
  }
  if (blob.size > 50 * 1024 * 1024) {
    return { ok: false, reason: 'too-large' };
  }
  try {
    const { PDFDocument } = await import('pdf-lib');
    const document = await PDFDocument.load(await blob.arrayBuffer(), {
      ignoreEncryption: false,
      updateMetadata: false,
    });
    const pages = document.getPages();
    if (!pages.length) return { ok: false, reason: 'no-pages' };
    const first = pages[0].getSize();
    const widthMm = (first.width / 72) * 25.4;
    const heightMm = (first.height / 72) * 25.4;
    const matchesTarget = shippingLabelPageSizeMatchesTarget({ widthMm, heightMm });
    return {
      ok: true,
      pageCount: pages.length,
      widthMm,
      heightMm,
      matchesTarget,
    };
  } catch {
    return { ok: false, reason: 'parse-failed' };
  }
}

/**
 * A2 contract: prompt only when name hints OR 4×6 geometry.
 * @param {{ name?: string, originalFileName?: string, fileName?: string, contentType?: string, blob?: Blob|null }} input
 */
export async function evaluateShippingLabelHeuristic(input = {}) {
  const nameLikely = shippingLabelNameLooksLikely(input);
  let geometry = null;
  if (input.blob instanceof Blob && input.blob.size) {
    geometry = await inspectShippingLabelPdfGeometry(input.blob);
  }
  const sizeLikely = geometry?.ok === true && geometry.matchesTarget === true;
  return {
    shouldPrompt: nameLikely || sizeLikely,
    nameLikely,
    sizeLikely,
    geometry,
  };
}
